// ISO3 → dashboard 5-region map, shared between server and web.
//
// Lifted out of `packages/web/src/components/dashboard/helpers.ts` (`regionOf`)
// so the connectivity pipeline can group `arr_iso → region` server-side and
// the web tier keeps rendering the same labels. The label set matches the
// `regionColors` keys the dashboard already uses: "Asia Pacific", "Europe",
// "Americas", "Middle East", "Africa".
//
// Coverage is comprehensive (every ISO3 the dashboard touches resolves) so
// the connectivity aggregation can assert "5 regions cover all countries" and
// log an "Other" bucket warning for anything that slips through. Grouping
// follows the dashboard's MENA convention rather than pure UN M49 — Egypt
// sits in "Middle East" (matching the existing mock + regionColors), the rest
// of North Africa stays in "Africa".

export const REGION_LABELS = ["Asia Pacific", "Europe", "Americas", "Middle East", "Africa"] as const;
export type RegionLabel = (typeof REGION_LABELS)[number];

const EUROPE: readonly string[] = [
  "ALB",
  "AND",
  "ARM",
  "AUT",
  "AZE",
  "BEL",
  "BGR",
  "BIH",
  "BLR",
  "CHE",
  "CYP",
  "CZE",
  "DEU",
  "DNK",
  "ESP",
  "EST",
  "FIN",
  "FRA",
  "FRO",
  "GBR",
  "GEO",
  "GIB",
  "GRC",
  "HRV",
  "HUN",
  "IRL",
  "ISL",
  "ITA",
  "KAZ",
  "LIE",
  "LTU",
  "LUX",
  "LVA",
  "MCO",
  "MDA",
  "MKD",
  "MLT",
  "MNE",
  "NLD",
  "NOR",
  "POL",
  "PRT",
  "ROU",
  "RUS",
  "SMR",
  "SRB",
  "SVK",
  "SVN",
  "SWE",
  "TUR",
  "UKR",
  "VAT",
  "XKX",
  // Crown dependencies + European territories that appear in aviation data.
  "JEY",
  "GGY",
  "IMN",
  "ALA",
  "SJM",
];

const ASIA_PACIFIC: readonly string[] = [
  "AFG",
  "AUS",
  "BGD",
  "BRN",
  "BTN",
  "CHN",
  "COK",
  "FJI",
  "FSM",
  "HKG",
  "IDN",
  "IND",
  "JPN",
  "KGZ",
  "KHM",
  "KIR",
  "KOR",
  "LAO",
  "LKA",
  "MAC",
  "MDV",
  "MHL",
  "MMR",
  "MNG",
  "MYS",
  "NPL",
  "NRU",
  "NZL",
  "NIU",
  "PAK",
  "PCN",
  "PHL",
  "PLW",
  "PNG",
  "PRK",
  "SGP",
  "SLB",
  "TJK",
  "THA",
  "TKL",
  "TKM",
  "TLS",
  "TON",
  "TUV",
  "TWN",
  "UZB",
  "VNM",
  "VUT",
  "WSM",
  // Pacific + Indian Ocean territories that appear in aviation data.
  "GUM",
  "MNP",
  "ASM",
  "UMI",
  "PYF",
  "NCL",
  "WLF",
  "CCK",
  "CXR",
  "NFK",
  "IOT",
];

const AMERICAS: readonly string[] = [
  "AIA",
  "ABW",
  "ARG",
  "ATG",
  "BES",
  "BHS",
  "BLZ",
  "BLM",
  "BMU",
  "BOL",
  "BRA",
  "BRB",
  "CAN",
  "CHL",
  "COL",
  "CRI",
  "CUB",
  "CUW",
  "CYM",
  "DMA",
  "DOM",
  "ECU",
  "FLK",
  "GLP",
  "GRD",
  "GRL",
  "GTM",
  "GUF",
  "GUY",
  "HND",
  "HTI",
  "JAM",
  "KNA",
  "LCA",
  "MAF",
  "MEX",
  "MSR",
  "MTQ",
  "NIC",
  "PAN",
  "PER",
  "PRI",
  "PRY",
  "SLV",
  "SPM",
  "SUR",
  "SXM",
  "TCA",
  "TTO",
  "URY",
  "USA",
  "VCT",
  "VEN",
  "VGB",
  "VIR",
];

const MIDDLE_EAST: readonly string[] = [
  "ARE",
  "BHR",
  "EGY",
  "IRN",
  "IRQ",
  "ISR",
  "JOR",
  "KWT",
  "LBN",
  "OMN",
  "PSE",
  "QAT",
  "SAU",
  "SYR",
  "YEM",
];

const AFRICA: readonly string[] = [
  "AGO",
  "BDI",
  "BEN",
  "BFA",
  "BWA",
  "CAF",
  "CIV",
  "CMR",
  "COD",
  "COG",
  "COM",
  "CPV",
  "DJI",
  "DZA",
  "ERI",
  "ESH",
  "ETH",
  "GAB",
  "GHA",
  "GIN",
  "GMB",
  "GNB",
  "GNQ",
  "KEN",
  "LBR",
  "LBY",
  "LSO",
  "MAR",
  "MDG",
  "MLI",
  "MOZ",
  "MRT",
  "MUS",
  "MWI",
  "MYT",
  "NAM",
  "NER",
  "NGA",
  "REU",
  "RWA",
  "SDN",
  "SEN",
  "SHN",
  "SLE",
  "SOM",
  "SSD",
  "STP",
  "SWZ",
  "SYC",
  "TCD",
  "TGO",
  "TUN",
  "TZA",
  "UGA",
  "ZAF",
  "ZMB",
  "ZWE",
];

const REGION_OF: Record<string, RegionLabel> = {};
for (const iso of EUROPE) REGION_OF[iso] = "Europe";
for (const iso of ASIA_PACIFIC) REGION_OF[iso] = "Asia Pacific";
for (const iso of AMERICAS) REGION_OF[iso] = "Americas";
for (const iso of MIDDLE_EAST) REGION_OF[iso] = "Middle East";
for (const iso of AFRICA) REGION_OF[iso] = "Africa";

/** Region for an ISO3 country, or null if unmapped (caller buckets as "Other"). */
export function regionOfIso3(iso3: string): RegionLabel | null {
  return REGION_OF[iso3.toUpperCase()] ?? null;
}

export { REGION_OF };
